import type { Image3D } from '~/domain/patient/image3d';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';
import { seriesNumberOf } from '~/utils/transforms/imageDataTransforms';
import { PairEnd, pairEndLabels } from '../../pairEndLabels';

/** The console mirror of {@link warnFusionRefused}: names no image at all. */
const FUSION_REFUSED_LOG_MESSAGE =
    'A fusion on the viewport grid was refused: an image of the pair is not ' +
    'loaded, or the two images share no frame of reference and no ' +
    "registration from the primary image's frame.";

/** How an image is named in a pair: its modality and series number. */
export function pairEndOf(image: Image3D): PairEnd {
    return {
        modality: String(image.seriesMetadata.modality),
        seriesNumber: seriesNumberOf(image),
    };
}

/**
 * Tells the user that `secondary` was not fused onto the row of `primary`,
 * naming both by modality, and by series number when the modalities match.
 * Neither is PHI; the console mirror names neither image all the same.
 */
export function warnFusionRefused(primary: PairEnd, secondary: PairEnd): void {
    const [primaryLabel, secondaryLabel] = pairEndLabels(
        primary,
        secondary,
        (seriesNumber) =>
            localization.t('series.number', { number: String(seriesNumber) })
    );
    enqueueSnackbar(
        localization.t('fusion.refused', {
            primary: primaryLabel,
            secondary: secondaryLabel,
        }),
        { variant: 'warning', logMessage: FUSION_REFUSED_LOG_MESSAGE }
    );
}
